import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useMenuSettings } from '../../../hooks/useMenuSettings';

const MenuControlPanel = () => {
  const [localLoading, setLocalLoading] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [user, setUser] = useState(null);
  
  const { menuSettings, loading, error, toggleMenuVisibility, refresh } = useMenuSettings(true);

  // Check localStorage authentication (matching Admin Dashboard pattern)
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        setUser(null);
      }
    }
  }, []);

  // Check if user is admin using the same logic as Admin Dashboard
  const isAdmin = user?.type === 'admin';

  const handleToggle = async (menuKey, currentVisibility) => {
    if (!isAdmin) {
      setFeedback('Only administrators can modify menu settings');
      setTimeout(() => setFeedback(''), 3000);
      return;
    }

    setLocalLoading(menuKey);
    setFeedback('');

    try {
      const success = await toggleMenuVisibility(menuKey);
      
      if (success) {
        const action = currentVisibility ? 'hidden' : 'shown';
        setFeedback(`Menu item successfully ${action}`);
      } else {
        setFeedback('Failed to update menu visibility');
      }
    } catch (error) {
      setFeedback('An error occurred while updating menu');
    } finally {
      setLocalLoading(null);
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  const getMenuIcon = (menuKey) => {
    const iconMap = {
      'shop': 'ShoppingBag',
      'gallery': 'Image',
      'resources': 'BookOpen',
      'services': 'Wrench',
      'contact': 'MessageCircle',
      'about': 'Users'
    };
    return iconMap?.[menuKey] || 'Menu';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Settings" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-text-primary">Menu Visibility Control</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-text-secondary">Loading menu settings...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Settings" size={20} color="var(--color-error)" />
          <h3 className="text-lg font-semibold text-text-primary">Menu Visibility Control</h3>
        </div>
        <div className="text-center py-8">
          <Icon name="AlertCircle" size={24} color="var(--color-error)" className="mx-auto mb-2" />
          <p className="text-error text-sm mb-4">{error}</p>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={refresh}
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow-elevation-2 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <Icon name="Shield" size={20} color="var(--color-warning)" />
          <h3 className="text-lg font-semibold text-text-primary">Menu Visibility Control</h3>
        </div>
        <div className="text-center py-8">
          <Icon name="Lock" size={24} color="var(--color-warning)" className="mx-auto mb-2" />
          <p className="text-text-secondary">
            Administrator privileges required to manage menu visibility settings.
          </p>
          {!user && (
            <p className="text-text-secondary text-sm mt-2">
              Please sign in to access admin features.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-elevation-2 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Icon name="Settings" size={20} color="var(--color-primary)" />
          <h3 className="text-lg font-semibold text-text-primary">Menu Visibility Control</h3>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-text-secondary">Logged in as: {user?.name || 'Admin'}</span>
          <Button
            variant="ghost"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={refresh}
            className="text-text-secondary hover:text-text-primary"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Feedback Message */}
      {feedback && (
        <div className={`mb-4 p-3 rounded-lg ${
          feedback?.includes('success') 
            ? 'bg-success/10 text-success border border-success/20' :'bg-error/10 text-error border border-error/20'
        }`}>
          <p className="text-sm">{feedback}</p>
        </div>
      )}

      <div className="space-y-4">
        {menuSettings?.map((menu) => (
          <div
            key={menu?.id}
            className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors duration-200"
          >
            <div className="flex items-center space-x-3">
              <Icon
                name={getMenuIcon(menu?.menu_key)}
                size={20}
                color={menu?.is_visible ? "var(--color-primary)" : "var(--color-text-secondary)"}
              />
              <div>
                <h4 className="text-sm font-medium text-text-primary">{menu?.menu_label}</h4>
                <p className="text-xs text-text-secondary mt-1">
                  {menu?.description || `Control visibility of ${menu?.menu_label} in navigation`}
                </p>
                {menu?.updated_at && (
                  <p className="text-xs text-text-secondary mt-1">
                    Last updated: {new Date(menu?.updated_at)?.toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <span
                className={`text-xs px-2 py-1 rounded-full font-medium ${
                  menu?.is_visible
                    ? 'bg-success/10 text-success border border-success/20' :'bg-error/10 text-error border border-error/20'
                }`}
              >
                {menu?.is_visible ? 'Visible' : 'Hidden'}
              </span>
              
              <Button
                variant={menu?.is_visible ? "outline" : "default"}
                size="sm"
                iconName={menu?.is_visible ? "EyeOff" : "Eye"}
                iconPosition="left"
                onClick={() => handleToggle(menu?.menu_key, menu?.is_visible)}
                disabled={localLoading === menu?.menu_key}
                className={
                  menu?.is_visible 
                    ? "text-error border-error hover:bg-error hover:text-white" :"bg-success text-white hover:bg-success/90"
                }
              >
                {localLoading === menu?.menu_key ? (
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                ) : (
                  menu?.is_visible ? 'Hide' : 'Show'
                )}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {menuSettings?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Menu" size={24} color="var(--color-text-secondary)" className="mx-auto mb-2" />
          <p className="text-text-secondary">No menu settings found.</p>
        </div>
      )}
    </div>
  );
};

export default MenuControlPanel;